import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Plus, ChevronDown } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { AnchoredMenu } from 'components/ui/AnchoredMenu'
import { cn } from 'lib/utils'

export interface LanguageItem {
  language?: string
  level?: string
}

interface LanguageFieldProps {
  items: LanguageItem[]
  onItemsChange: (items: LanguageItem[]) => void
  onBlur?: () => void
  placeholder?: string
  label?: string
  description?: string
  error?: string
  required?: boolean
  className?: string
  disabled?: boolean
  readOnly?: boolean
}

const COMMON_LANGUAGES = [
  'Anglais', 'Français', 'Espagnol', 'Allemand', 'Italien', 'Portugais', 
  'Arabe', 'Chinois', 'Japonais', 'Russe', 'Néerlandais', 'Suédois',
  'Norvégien', 'Danois', 'Finnois', 'Polonais', 'Tchèque', 'Hongrois',
  'Roumain', 'Bulgare', 'Grec', 'Turc', 'Hébreu', 'Hindi', 'Coréen'
]

const PROFICIENCY_LEVELS = [
  { value: 'A1', label: 'A1 - Débutant' },
  { value: 'A2', label: 'A2 - Élémentaire' },
  { value: 'B1', label: 'B1 - Intermédiaire' },
  { value: 'B2', label: 'B2 - Intermédiaire avancé' },
  { value: 'C1', label: 'C1 - Avancé' },
  { value: 'C2', label: 'C2 - Maîtrise' },
  { value: 'Natif', label: 'Natif' }
]

export function LanguageField({
  items = [],
  onItemsChange,
  onBlur,
  placeholder = "Sélectionnez une langue...",
  label,
  description,
  error,
  required = false,
  className,
  disabled = false,
  readOnly = false
}: LanguageFieldProps) {
  const isReadOnly = readOnly || disabled
  const { t } = useTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState('')
  const [selectedLevel, setSelectedLevel] = useState('')
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const [showLevelDropdown, setShowLevelDropdown] = useState(false)
  const [customLanguage, setCustomLanguage] = useState('')
  const languageAnchorRef = useRef<HTMLDivElement>(null)
  const levelAnchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showLanguageDropdown && !showLevelDropdown) return
    const close = (event: MouseEvent) => {
      const target = event.target as Node
      if (languageAnchorRef.current?.contains(target) || levelAnchorRef.current?.contains(target)) return
      setShowLanguageDropdown(false)
      setShowLevelDropdown(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [showLanguageDropdown, showLevelDropdown])

  const handleAddLanguage = () => {
    const language = selectedLanguage || (customLanguage || '').trim()
    const level = selectedLevel

    if (language && level && !items.some(item => 
      item && (item.language || '').toLowerCase() === language.toLowerCase() && item.level === level
    )) {
      onItemsChange([...items, { language: language.trim(), level }])
      setSelectedLanguage('')
      setSelectedLevel('')
      setCustomLanguage('')
      setShowLanguageDropdown(false)
      setShowLevelDropdown(false)
    }
  }

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index)
    onItemsChange(newItems)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddLanguage()
    }
  }

  const filteredLanguages = COMMON_LANGUAGES.filter(lang =>
    lang.toLowerCase().includes((selectedLanguage || '').toLowerCase())
  )

  const isDuplicate = items.some(item => 
    item && (item.language || '').toLowerCase() === (selectedLanguage || customLanguage || '').toLowerCase() && 
    item.level === selectedLevel
  )

  const canAdd = (selectedLanguage || (customLanguage || '').trim()) && selectedLevel && !isDuplicate

  return (
    <div className={cn("space-y-3", className)}>
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {/* Language and Level Selection */}
      {!isReadOnly && (
        <div className="space-y-3">
          {/* Language Selection */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="flex-1 relative" ref={languageAnchorRef}>
                <Input
                  value={selectedLanguage}
                  onChange={(e) => {
                    setSelectedLanguage(e.target.value)
                    setCustomLanguage('')
                    setShowLanguageDropdown(true)
                    setShowLevelDropdown(false)
                  }}
                  onFocus={() => {
                    setShowLanguageDropdown(true)
                    setShowLevelDropdown(false)
                  }}
                  onBlur={() => {
                    setTimeout(() => onBlur?.(), 200)
                  }}
                  placeholder={t('createOffer.form.languageSearchPlaceholder')}
                  className="pr-8"
                />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <AnchoredMenu anchorRef={languageAnchorRef} open={showLanguageDropdown}>
                  {filteredLanguages.length > 0 ? (
                    filteredLanguages.map((language) => (
                      <button
                        key={language}
                        type="button"
                        className="w-full break-words px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setSelectedLanguage(language)
                          setShowLanguageDropdown(false)
                        }}
                      >
                        {language}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      {t('createOffer.form.noLanguagesFound')}
                    </div>
                  )}
                </AnchoredMenu>
              </div>
            </div>
            
            {/* Custom Language Input */}
            {selectedLanguage && !COMMON_LANGUAGES.includes(selectedLanguage) && (
              <div className="mt-2">
                <Input
                  value={customLanguage}
                  onChange={(e) => setCustomLanguage(e.target.value)}
                  placeholder={t('createOffer.form.customLanguagePlaceholder')}
                  className="text-sm"
                />
              </div>
            )}
          </div>

          {/* Level Selection */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="flex-1 relative" ref={levelAnchorRef}>
                <Input
                  value={PROFICIENCY_LEVELS.find((level) => level.value === selectedLevel)?.label || ''}
                  onFocus={() => {
                    setShowLevelDropdown(true)
                    setShowLanguageDropdown(false)
                  }}
                  onBlur={() => {
                    setTimeout(() => onBlur?.(), 200)
                  }}
                  placeholder={t('createOffer.form.levelSelectPlaceholder')}
                  className="pr-8"
                  readOnly
                />
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <AnchoredMenu anchorRef={levelAnchorRef} open={showLevelDropdown}>
                  {PROFICIENCY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      className="w-full break-words px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSelectedLevel(level.value)
                        setShowLevelDropdown(false)
                      }}
                    >
                      {level.label}
                    </button>
                  ))}
                </AnchoredMenu>
              </div>
              
              <Button
                type="button"
                onClick={handleAddLanguage}
                disabled={!canAdd}
                size="sm"
                className="px-3"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Display language items */}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            item && (
            <div
              key={index}
              className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary rounded-full text-sm border border-primary/20 dark:border-primary/30"
            >
              <span className="font-medium">{item.language || ''}</span>
              <span className="text-xs bg-primary/20 dark:bg-primary/30 px-2 py-0.5 rounded-full">
                {item.level || ''}
              </span>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="hover:bg-primary/20 dark:hover:bg-primary/30 rounded-full p-0.5 transition-colors"
                  title={t('common.delete', 'Delete')}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            )
          ))}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
